import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PrinterIcon,
  XMarkIcon,
  TrashIcon,
  NoSymbolIcon,
  CameraIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { weeklyProgramsAPI, workoutPlannerAPI } from '../../services/api'
import { InlineSessionContent } from './InlineSessionContent'
import { SessionLocationPrompt } from '../capture/SessionLocationPrompt'
import { PrintViewModal } from './PrintViewModal'
import { ExercisePickerModal } from './ExercisePickerModal'
import { SessionScoreDisplay } from './SessionScoreDisplay'
import { SessionQuantityPicker } from './SessionQuantityPicker'

export function ProgramDetailModal({ program, onClose }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [expandedSessions, setExpandedSessions] = useState(new Set())
  // VH-232: the ONE session the location question is about, set only when the
  // athlete taps to open a single session. Expand All does not set it — the
  // overview can open every session at once, and a question per session would
  // stack a dialog each.
  const [promptSessionId, setPromptSessionId] = useState(null)
  const [showPrintView, setShowPrintView] = useState(false)
  const [showAddSession, setShowAddSession] = useState(false)
  const [showAddSessionDialog, setShowAddSessionDialog] = useState(false)
  // VH bug ⑫: user-controlled session ordering. 'name' is the default (matches
  // the backend's default order_by); 'dateAsc' / 'dateDesc' order by the
  // session date so a missed day is easy to spot.
  const [sortMode, setSortMode] = useState('name')
  const isGerman = i18n.language === 'de'

  const toggleSessionExpand = (sessionId) => {
    // Opening (not closing) a single session is the gesture that means "I am
    // about to log this one", so it is the one that may ask where.
    if (!expandedSessions.has(sessionId)) {
      setPromptSessionId(sessionId)
    } else if (promptSessionId === sessionId) {
      setPromptSessionId(null)
    }
    setExpandedSessions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId)
      } else {
        newSet.add(sessionId)
      }
      return newSet
    })
  }

  const expandAll = () => {
    const allIds = new Set(fullProgram.sessions?.map(s => s.id) || [])
    setExpandedSessions(allIds)
  }

  const collapseAll = () => {
    setExpandedSessions(new Set())
  }

  const { data: programData, isLoading } = useQuery({
    queryKey: ['programDetail', program.id],
    queryFn: () => weeklyProgramsAPI.getProgram(program.id)
  })

  // VH-110: fetch user's TrainingTemplates for the "Add session" dialog
  const { data: templatesData } = useQuery({
    queryKey: ['exerciseModules'],
    queryFn: () => workoutPlannerAPI.getTemplates({})
  })
  const templates = templatesData?.data?.templates || []

  const addExerciseMutation = useMutation({
    mutationFn: (data) => weeklyProgramsAPI.addExerciseFromLibrary(program.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      setShowAddSession(false)
      toast.success(t('programs.exerciseAdded') || 'Exercise added')
    },
    onError: (error) => {
      console.error('Failed to add exercise:', error)
      toast.error(error.response?.data?.detail || 'Failed to add exercise')
    }
  })

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId) => weeklyProgramsAPI.deleteSession(program.id, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
    }
  })

  const refreshTargetsMutation = useMutation({
    mutationFn: () => weeklyProgramsAPI.refreshTargets(program.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
      toast.success(t('programs.refreshTargets.success') || 'Targets refreshed')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('programs.refreshTargets.error') || 'Failed to refresh targets')
    }
  })

  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation()
    if (window.confirm(t('programs.confirmDeleteSession'))) {
      deleteSessionMutation.mutate(sessionId)
    }
  }

  const fullProgram = programData?.data || program

  // VH bug ⑫: sort a COPY of the sessions for display so the underlying data
  // is never mutated. 'name' uses a stable localeCompare on the module name;
  // the date modes key off actual_date and fall back to planned_day when the
  // session hasn't been dated yet. Sessions with no date sort to the end.
  const getSessionDateKey = (s) => {
    if (s.actual_date) return new Date(s.actual_date).getTime()
    if (s.planned_day != null) return s.planned_day
    return null
  }
  const sortedSessions = [...(fullProgram.sessions || [])].sort((a, b) => {
    if (sortMode === 'name') {
      return (a.snapshot_module_name_en || '').localeCompare(b.snapshot_module_name_en || '')
    }
    const ak = getSessionDateKey(a)
    const bk = getSessionDateKey(b)
    if (ak == null && bk == null) return 0
    if (ak == null) return 1
    if (bk == null) return -1
    return sortMode === 'dateDesc' ? bk - ak : ak - bk
  })

  if (showPrintView) {
    return (
      <PrintViewModal
        program={fullProgram}
        onClose={() => setShowPrintView(false)}
        onBack={() => setShowPrintView(false)}
      />
    )
  }

  if (showAddSession) {
    return (
      <ExercisePickerModal
        program={fullProgram}
        onSave={(data) => addExerciseMutation.mutate(data)}
        onClose={() => setShowAddSession(false)}
        isLoading={addExerciseMutation.isPending}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-surface-container z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('programs.week')} {fullProgram.week_number}, {fullProgram.year}
            </h2>
            <p className="text-sm text-gray-500">
              {new Date(fullProgram.week_start_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit' })} - {new Date(fullProgram.week_end_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {fullProgram.sessions && fullProgram.sessions.length > 1 && (
              <>
                <button
                  onClick={expandAll}
                  className="btn-outline btn-sm flex items-center text-xs"
                  title={t('programs.expandAll')}
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={collapseAll}
                  className="btn-outline btn-sm flex items-center text-xs"
                  title={t('programs.collapseAll')}
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </button>
              </>
            )}
            {fullProgram.status === 'draft' && (
              <button
                onClick={() => refreshTargetsMutation.mutate()}
                disabled={refreshTargetsMutation.isPending}
                className="btn-outline btn-sm flex items-center"
                title={t('programs.refreshTargets.title') || 'Refresh Targets'}
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${refreshTargetsMutation.isPending ? 'animate-spin' : ''}`} />
                {t('programs.refreshTargets.title') || 'Refresh Targets'}
              </button>
            )}
            {/* Print waits for the program DETAIL. Until it arrives the modal
                shows the list payload, which carries no VH-207 aim flags — a
                sheet printed in that window came out without its aim markers
                (UAT 2026-09-14), and paper cannot be corrected afterwards. */}
            <button
              onClick={() => setShowPrintView(true)}
              disabled={!programData}
              className="btn-outline btn-sm flex items-center disabled:opacity-50 disabled:cursor-wait"
              title={t('programs.print.title')}
            >
              <PrinterIcon className="h-4 w-4 mr-1" />
              {t('programs.print.title')}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {fullProgram.weekly_notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-800">{t('programs.form.weeklyNotes')}</p>
                  <p className="text-sm text-yellow-700 mt-1">{fullProgram.weekly_notes}</p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900">{t('programs.sessions')}</h3>
                    {fullProgram.sessions && fullProgram.sessions.length > 1 && (
                      <select
                        value={sortMode}
                        onChange={(e) => setSortMode(e.target.value)}
                        className="text-sm px-2 py-1 text-gray-700 bg-surface-container"
                        data-testid="session-sort-select"
                        aria-label={t('programs.sortByName')}
                      >
                        <option value="name">{t('programs.sortByName')}</option>
                        <option value="dateAsc">{t('programs.sortDateAsc')}</option>
                        <option value="dateDesc">{t('programs.sortDateDesc')}</option>
                      </select>
                    )}
                  </div>
                  {fullProgram.status === 'draft' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowAddSessionDialog(true)}
                        className="btn-primary btn-sm flex items-center"
                        data-testid="open-add-session-dialog"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        {t('programs.addSession.button')}
                      </button>
                      <button
                        onClick={() => setShowAddSession(true)}
                        className="btn-outline btn-sm flex items-center"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        {t('programs.addExercise')}
                      </button>
                    </div>
                  )}
                </div>
                {fullProgram.sessions && fullProgram.sessions.length > 0 ? (
                  sortedSessions.map((session) => {
                    const skippedCount = session.exercises?.filter(e => e.is_skipped).length || 0
                    const unplannedCount = session.exercises?.filter(e => e.is_unplanned).length || 0
                    const isExpanded = expandedSessions.has(session.id)

                    return (
                      <div
                        key={session.id}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          session.is_completed
                            ? 'border-green-200'
                            : 'border-gray-200'
                        }`}
                      >
                        <div
                          className={`p-4 cursor-pointer transition-colors ${
                            session.is_completed
                              ? 'bg-green-50 hover:bg-green-100'
                              : isExpanded
                                ? 'bg-primary-50'
                                : 'bg-surface-container hover:bg-gray-50'
                          }`}
                          onClick={() => toggleSessionExpand(session.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {session.snapshot_module_color && (
                                <div
                                  className="w-4 h-4 rounded-full mr-3"
                                  style={{ backgroundColor: session.snapshot_module_color }}
                                />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{session.snapshot_module_name_en}</p>
                                  {session.intake_token && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        const tokenText = `wk-${session.intake_token}`
                                        if (navigator.clipboard?.writeText) {
                                          navigator.clipboard.writeText(tokenText)
                                        }
                                        toast.success(t('programs.session.tokenCopied'))
                                      }}
                                      title={`wk-${session.intake_token}`}
                                      data-testid={`session-token-${session.id}`}
                                      className="font-mono text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                    >
                                      wk-{session.intake_token}
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">
                                  {session.planned_day && t(`programs.dayOfWeek.${session.planned_day}`)}
                                  {session.exercises && ` • ${session.exercises.length} ${t('trainingDays.exercises')}`}
                                  {session.snapshot_estimated_duration && ` • ${session.snapshot_estimated_duration} ${t('common.minutes')}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {session.is_completed && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                  {t('trainingDays.completed')}
                                </span>
                              )}
                              {skippedCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                  <NoSymbolIcon className="h-3.5 w-3.5 mr-1" />
                                  {skippedCount} {t('programs.skipped')}
                                </span>
                              )}
                              {unplannedCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                  <PlusIcon className="h-3.5 w-3.5 mr-1" />
                                  {unplannedCount} {t('programs.unplanned')}
                                </span>
                              )}
                              <SessionScoreDisplay
                                sessionId={session.id}
                                isSubmitted={fullProgram.status === 'submitted'}
                              />
                              {fullProgram.status !== 'submitted' && !session.is_completed && (
                                <div className="flex items-center gap-1 ml-2 border-l border-gray-200 pl-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleSessionExpand(session.id); }}
                                    className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded"
                                    title={t('workouts.photo.scanResults', 'Scan Results')}
                                  >
                                    <CameraIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleSessionExpand(session.id); }}
                                    className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded"
                                    title={t('trainingDays.markComplete')}
                                  >
                                    <CheckCircleIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                              {fullProgram.status !== 'submitted' && (
                                <button
                                  onClick={(e) => handleDeleteSession(e, session.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title={t('common.delete')}
                                  disabled={deleteSessionMutation.isPending}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                              {isExpanded ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <InlineSessionContent
                            session={session}
                            program={fullProgram}
                            queryClient={queryClient}
                          />
                        )}
                      </div>
                    )
                  })
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {t('templates.noExercises')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddSessionDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('programs.sessionPicker.title')}
              </h2>
              <button
                onClick={() => setShowAddSessionDialog(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              <SessionQuantityPicker
                templates={templates}
                onSubmit={async (quantityMap, onProgress) => {
                  // Compute total ops
                  let total = 0
                  for (const n of Object.values(quantityMap)) total += Number(n) || 0
                  let current = 0
                  try {
                    for (const [tid, count] of Object.entries(quantityMap)) {
                      const n = Number(count) || 0
                      const templateId = Number(tid)
                      for (let i = 0; i < n; i++) {
                        current += 1
                        onProgress(current, total)
                        await weeklyProgramsAPI.addSession(program.id, {
                          template_id: templateId,
                          planned_day: null,
                        })
                      }
                    }
                    queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
                    queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
                    setShowAddSessionDialog(false)
                  } catch (err) {
                    const detail = err?.response?.data?.detail
                    toast.error(detail || t('programs.sessionPicker.addError'))
                    // Still refetch — some sessions may have landed before failure.
                    queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
                    queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* VH-232: the same question the capture sheet asks (VH-219), from the
          same component, so the two doors cannot ask differently. Rendered
          once, here, rather than inside each expanded session — which is what
          guarantees at most one dialog. The component itself stays silent
          when the location is already confirmed, skipped, or the athlete has
          fewer than two gyms. */}
      {(() => {
        const s = promptSessionId && (fullProgram.sessions || []).find((x) => x.id === promptSessionId)
        if (!s || !s.intake_token || s.is_completed || fullProgram.status === 'submitted') return null
        return (
          <SessionLocationPrompt
            key={`${s.id}-${s.resolved_location_id ?? s.location_id ?? 'none'}`}
            token={s.intake_token}
            sessionName={s.snapshot_module_name_en}
            locationId={s.resolved_location_id ?? s.location_id ?? null}
            isOverride={Boolean(s.location_is_override)}
            onDone={() => {
              setPromptSessionId(null)
              queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
            }}
          />
        )
      })()}
    </div>
  )
}
